import { useCallback, useEffect, useRef } from 'react';

export interface UseDateDragToFillOptions {
  /** Check if a row at a given index is locked for the field being dragged */
  getRowLocked: (rowIndex: number, field: string) => boolean;
  /** Called for each row in the fill range on mouseup; may return a promise */
  updateRow: (rowIndex: number, field: string, value: string) => void | Promise<void>;
  /** Optional batch handler, allowing a single confirmation for the entire fill. */
  fillRows?: (rows: number[], field: string, value: string, sourceRow: number) => Promise<void>;
  /** Called after the drag completes and all row update promises have settled */
  onDragEnd?: () => void;
  /** CSS selector for the scrollable table container */
  scrollContainerSelector?: string;
}

export interface UseDateDragToFillReturn {
  startDrag: (rowIndex: number, field: string, value: string) => void;
  /** Ref that is true while the user is actively dragging */
  isDraggingRef: React.RefObject<boolean>;
}

/**
 * Drag-to-fill hook for date columns.
 *
 * All fill-range tracking and highlighting is done via refs and direct DOM
 * manipulation so that dragging never triggers React re-renders.  This avoids
 * the cascade where a state update → column useMemo recompute → full table
 * re-render → DatePickers unmount/remount ("disappearing dates").
 */
export function useDateDragToFill(options: UseDateDragToFillOptions): UseDateDragToFillReturn {
  // Keep options in a ref so the effect never re-runs when callbacks change
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const sourceRowRef = useRef<number>(-1);
  const sourceValueRef = useRef<string>('');
  const dragFieldRef = useRef<string | null>(null);
  const isDraggingRef = useRef(false);
  const rafRef = useRef<number>(0);
  const scrollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fillRangeRef = useRef<Set<number>>(new Set());

  // One-time listener setup — everything reads from refs, no deps needed
  useEffect(() => {
    /** Apply or remove highlight on a row's cells (td elements have their own
     *  bg classes that paint over the tr, so we must style them directly). */
    const highlightRow = (idx: number, on: boolean) => {
      const tr = document.querySelector(`tr[data-row-index="${idx}"]`) as HTMLElement | null;
      if (!tr) return;
      tr.querySelectorAll<HTMLElement>('td').forEach(td => {
        td.style.backgroundColor = on ? '#DBEAFE' : '';
      });
    };

    /** Remove highlight from rows no longer in range, add to new rows */
    const applyHighlights = (newRows: Set<number>) => {
      fillRangeRef.current.forEach(idx => {
        if (!newRows.has(idx)) highlightRow(idx, false);
      });
      newRows.forEach(idx => {
        if (!fillRangeRef.current.has(idx)) highlightRow(idx, true);
      });
      fillRangeRef.current = newRows;
    };

    /** Clear all highlights */
    const clearHighlights = () => {
      fillRangeRef.current.forEach(idx => highlightRow(idx, false));
      fillRangeRef.current = new Set();
    };

    const computeRange = (_clientX: number, clientY: number) => {
      // Find the source row's DOM element to use its vertical position
      const sourceTr = document.querySelector(
        `tr[data-row-index="${sourceRowRef.current}"]`
      ) as HTMLElement | null;
      if (!sourceTr) return;
      const sourceBottom = sourceTr.getBoundingClientRect().bottom;

      // Only allow downward fill
      if (clientY <= sourceBottom) {
        applyHighlights(new Set());
        return;
      }

      // Collect all visible table rows and pick those visually between
      // the source row and the current mouse position
      const selector = optionsRef.current.scrollContainerSelector || '.tan-table-scroll-container';
      const container = document.querySelector(selector);
      const allRows = (container || document).querySelectorAll('tr[data-row-index]');

      const rows = new Set<number>();
      allRows.forEach(tr => {
        const rect = tr.getBoundingClientRect();
        const rowMid = rect.top + rect.height / 2;
        // Row must be visually below the source and above (or at) the cursor
        if (rowMid > sourceBottom && rowMid <= clientY) {
          const idx = parseInt(tr.getAttribute('data-row-index')!, 10);
          if (!isNaN(idx) && idx !== sourceRowRef.current && !optionsRef.current.getRowLocked(idx, dragFieldRef.current!)) {
            rows.add(idx);
          }
        }
      });
      applyHighlights(rows);
    };

    const cleanupDrag = () => {
      isDraggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      cancelAnimationFrame(rafRef.current);
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
    };

    const handleMouseUp = () => {
      if (!isDraggingRef.current) return;

      // Apply the fill value to all highlighted rows, collecting any promises
      const field = dragFieldRef.current;
      const value = sourceValueRef.current;
      const promises: Promise<void>[] = [];
      if (field && value && fillRangeRef.current.size && optionsRef.current.fillRows) {
        promises.push(optionsRef.current.fillRows([...fillRangeRef.current], field, value, sourceRowRef.current));
      } else if (field && value) {
        fillRangeRef.current.forEach((rowIndex) => {
          const result = optionsRef.current.updateRow(rowIndex, field, value);
          if (result && typeof result.then === 'function') {
            promises.push(result);
          }
        });
      }

      clearHighlights();
      cleanupDrag();

      // Wait for all API calls to settle before signaling drag end
      if (promises.length > 0) {
        Promise.allSettled(promises).then(() => {
          optionsRef.current.onDragEnd?.();
        });
      } else {
        optionsRef.current.onDragEnd?.();
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current) return;

      // Safety: if mouse button was released but we missed the mouseup event
      if (e.buttons === 0) {
        handleMouseUp();
        return;
      }

      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => computeRange(e.clientX, e.clientY));

      // Auto-scroll near edges
      const selector = optionsRef.current.scrollContainerSelector || '.tan-table-scroll-container';
      const container = document.querySelector(selector);
      if (container) {
        const rect = container.getBoundingClientRect();
        const edgeZone = 40;
        if (scrollIntervalRef.current) {
          clearInterval(scrollIntervalRef.current);
          scrollIntervalRef.current = null;
        }
        if (e.clientY > rect.bottom - edgeZone) {
          scrollIntervalRef.current = setInterval(() => container.scrollBy(0, 8), 16);
        } else if (e.clientY < rect.top + edgeZone) {
          scrollIntervalRef.current = setInterval(() => container.scrollBy(0, -8), 16);
        }
      }
    };

    // Safety: end drag on window blur or tab hide
    const handleBlur = () => {
      if (isDraggingRef.current) handleMouseUp();
    };
    const handleVisibilityChange = () => {
      if (document.hidden && isDraggingRef.current) handleMouseUp();
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('blur', handleBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      cancelAnimationFrame(rafRef.current);
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
      clearHighlights();
    };
  }, []);

  const startDrag = useCallback((rowIndex: number, field: string, value: string) => {
    if (isDraggingRef.current) return; // Already dragging
    sourceRowRef.current = rowIndex;
    sourceValueRef.current = value;
    dragFieldRef.current = field;
    isDraggingRef.current = true;
    document.body.style.cursor = 'crosshair';
    document.body.style.userSelect = 'none';
  }, []);

  return { startDrag, isDraggingRef };
}

export default useDateDragToFill;
